/* import __COLOCATED_TEMPLATE__ from './phone-settings.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import { action } from '@ember/object';
import type { FinVoiceVoice } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import type { FinVoiceVoiceLanguage } from 'embercom/objects/ai-agent/fin-voice/fin-voice-language-options';
import type { FinVoiceRunbook } from 'embercom/objects/ai-agent/fin-voice/fin-voice-runbook-options';
import type { FinVoiceNumberConfigurationHandoverConfiguration } from 'embercom/models/fin-voice/number-configuration';
import type { Schedule } from 'embercom/services/fin-voice-config-service';
import type FinVoiceService from 'embercom/services/fin-voice-service';

interface Args {
  finSetupRuleset: $TSFixMe;
  phoneNumberId?: string;
  numberConfiguration?: FinVoiceNumberConfiguration;
  updatePercentageRollout: (
    phoneNumberId: string,
    rollOutPercentageIn: number | undefined,
    rollOutPercentageNin: number | undefined,
  ) => Promise<void>;
  onPopoverInteraction?: () => void;
  hasContentReadyForFin: boolean;
}

interface Signature {
  Element: HTMLElement;
  Args: Args;
}

export default class PhoneSettings extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare finVoiceConfigService: FinVoiceConfigService;
  @service declare finVoiceService: FinVoiceService;

  @tracked openSectionId = '';

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get classificationAttributeDescriptors() {
    return this.finVoiceConfigService.conversationAttributeDescriptors;
  }

  @action
  onOpenHandoverSection() {
    this.openSectionId = 'phone-handover';
  }

  @action
  async updateFinBehavior(behavior: {
    voice: FinVoiceVoice;
    greeting: string;
    greetingOutOfOfficeHours?: string;
    language: FinVoiceVoiceLanguage;
  }) {
    if (!this.args.phoneNumberId) {
      return;
    }

    try {
      await this.finVoiceConfigService.updateVoiceBehavior(
        this.args.phoneNumberId,
        behavior.voice,
        behavior.greeting,
        behavior.greetingOutOfOfficeHours,
        behavior.language,
      );
      // Since this is used in the playground preview, update the playground service to reflect the new configuration
      this.finVoiceService.resetNumberConfiguration();
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      this.notifyFailure(error);
    }
  }

  @action
  async updateRunbook(runbook: FinVoiceRunbook, procedureId: string) {
    if (!this.args.phoneNumberId) {
      return;
    }

    try {
      await this.finVoiceConfigService.updateRunbook(this.args.phoneNumberId, runbook, procedureId);
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      this.notifyFailure(error);
    }
  }

  @action
  async updateFinIssueCategorization(attributes?: string[]) {
    if (!this.args.phoneNumberId) {
      return;
    }

    try {
      await this.finVoiceConfigService.updateFinIssueCategorization(
        this.args.phoneNumberId,
        attributes,
      );
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      this.notifyFailure(error);
    }
  }

  @action
  async updateAfterPhoneCallEnds(autoCloseWhenNotEscalated: boolean, defaultTeamId?: string) {
    if (!this.args.phoneNumberId || !this.numberConfiguration) {
      return;
    }

    try {
      await this.finVoiceConfigService.updateAfterPhoneCallEnds(
        this.numberConfiguration,
        autoCloseWhenNotEscalated,
        defaultTeamId,
      );
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      this.notifyFailure(error);
    }
  }

  @action
  async updateHandover(handoverConfig: FinVoiceNumberConfigurationHandoverConfiguration) {
    if (!this.args.phoneNumberId) {
      return;
    }

    try {
      await this.finVoiceConfigService.updateHandoverConfiguration(
        this.args.phoneNumberId,
        handoverConfig,
      );
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      this.notifyFailure(error);
    }
  }

  @action
  async updateOfficeHourSchedule(phoneNumberId: string, schedule: string) {
    try {
      await this.finVoiceConfigService.updateOfficeHoursSchedule(phoneNumberId, schedule);
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      this.notifyFailure(error);
    }
  }

  @action
  async addAndSaveNewOfficeHoursSchedule(phoneNumberId: string, schedule: Schedule) {
    try {
      await this.finVoiceConfigService.addAndSaveNewOfficeHoursSchedule(phoneNumberId, schedule);
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      this.notifyFailure(error);
    }
  }

  @action
  async updateAskForCsatRating(
    askForCsatRating: boolean,
    csatMessage: string,
    csatTtsLanguage: string,
  ) {
    if (!this.args.phoneNumberId) {
      return;
    }

    try {
      await this.finVoiceConfigService.updateAskForCsatRating(
        this.args.phoneNumberId,
        askForCsatRating,
        csatMessage,
        csatTtsLanguage,
      );
      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      this.notifyFailure(error);
    }
  }

  private notifyFailure(error: Error) {
    this.notificationsService.notifyResponseError(error, {
      default: this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.failure'),
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::PhoneSettings': typeof PhoneSettings;
  }
}
